import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react"
import { Row, Col, Spin, LucideIcon, message, Button, jsonToQueryString, useCRUDController, Modal } from './ot'
import { useNavigate, useSearchParams, } from "react-router-dom";
import BaseDocumentResponseCanvasEdit from './base_document_response_canvas_edit'
import dayjs from "dayjs";


const BaseCashManagementModal = ({
    setOpen = () => { },
    open = false,
    title = "詳細資訊",
    width = "1200px",
    record,               // ⭐ record.document_id 在這裡
    realTimeApiRoute,     // ⭐ 你傳的 API 在這裡
    axiosMode = "get",
    documentApiRoute = '/cash_management/backend/record_document/view',
    onFinish = ({ axiosMode, status, values }) => { },
}) => {

    const documentId = record?.document_id || null
    const [loading, setLoading] = useState(false)
    const [historicalData, setHistoricalData] = useState([]);


    const modalRender_ = () => {
        const historicalData = {
            period: {
                startDate: "2025-10-01",
                endDate: "2025-10-12",
                totalDays: 12,
                lastUpdate: "2025-10-12 23:59:59",
            },
            summary: {
                totalTransactions: 18942,
                totalAmount: 238567420.80,
                avgDailyTransactions: 1578.5,
                avgDailyAmount: 19880618.40,
                peakDayTransactions: 2156,
                peakDayAmount: 28945320.15,
            },
            trends: {
                weeklyGrowth: {
                    transactionGrowth: 12.3,
                    amountGrowth: 15.7,
                },
                monthlyComparison: {
                    thisMonth: { transactions: 18942, amount: 238567420.80 },
                    lastMonth: { transactions: 17234, amount: 214523680.50 },
                    growth: {
                        transactionGrowth: 9.91,
                        amountGrowth: 11.22,
                    }
                }
            },
            refundHistory: {
                totalRefunds: 456,
                totalRefundAmount: 5847620.25,
                avgDailyRefunds: 38,
                refundRate: 2.41,
                peakRefundDay: 67,
            },
            dailyBreakdown: [
                { date: "2025-10-12", transactions: 1247, amount: 15678420.50, refunds: 34, status: "正常" },
                { date: "2025-10-11", transactions: 1189, amount: 14523680.20, refunds: 29, status: "正常" },
                { date: "2025-10-10", transactions: 2156, amount: 28945320.15, refunds: 67, status: "高峰" },
                { date: "2025-10-09", transactions: 1842, amount: 22156780.60, refunds: 45, status: "正常" },
                { date: "2025-10-08", transactions: 1654, amount: 19834520.30, refunds: 38, status: "正常" },
                { date: "2025-10-07", transactions: 1098, amount: 12845620.85, refunds: 22, status: "低峰" },
            ],
            channelPerformance: [
                { name: "信用卡", totalCount: 8234, totalAmount: 102456780.30, avgDaily: 686.2, trend: "穩定" },
                { name: "第三方支付", totalCount: 6156, totalAmount: 89234560.40, avgDaily: 513.0, trend: "上升" },
                { name: "銀行轉帳", totalCount: 3420, totalAmount: 35876540.80, avgDaily: 285.0, trend: "穩定" },
            ]
        }

        return (
            <>
                <div className="space-y-6">
                    {/* 歷史統計概覽 */}
                    <div className="bg-muted rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-lg font-semibold text-foreground">歷史交易統計</span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                {/* <Clock className="h-3 w-3" /> */}
                                統計期間: {historicalData.period.startDate} ~ {historicalData.period.endDate}
                            </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">
                                    {historicalData.summary.totalTransactions.toLocaleString()}
                                </div>
                                <div className="text-xs text-muted-foreground">總交易筆數</div>
                                <div className="text-xs text-blue-500 mt-1">
                                    日均 {Math.round(historicalData.summary.avgDailyTransactions).toLocaleString()} 筆
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                    ${historicalData.summary.totalAmount.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-muted-foreground">總交易金額</div>
                                <div className="text-xs text-green-500 mt-1">
                                    日均 ${Math.round(historicalData.summary.avgDailyAmount).toLocaleString('zh-TW')}
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-orange-600">
                                    {historicalData.summary.peakDayTransactions.toLocaleString()}
                                </div>
                                <div className="text-xs text-muted-foreground">單日最高筆數</div>
                                <div className="text-xs text-orange-500 mt-1">
                                    10/10 創下記錄
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-purple-600">
                                    ${historicalData.summary.peakDayAmount.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-muted-foreground">單日最高金額</div>
                                <div className="text-xs text-purple-500 mt-1">
                                    同樣在 10/10
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 趨勢分析 */}
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 space-y-3">
                        <h3 className="text-md font-medium text-foreground">趨勢分析</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white rounded p-3">
                                <div className="text-sm font-medium text-muted-foreground mb-2">週增長率</div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm">交易筆數:</span>
                                    <span className={`text-sm font-semibold px-2 py-1 rounded ${historicalData.trends.weeklyGrowth.transactionGrowth > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {historicalData.trends.weeklyGrowth.transactionGrowth > 0 ? '+' : ''}{historicalData.trends.weeklyGrowth.transactionGrowth}%
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-sm">交易金額:</span>
                                    <span className={`text-sm font-semibold px-2 py-1 rounded ${historicalData.trends.weeklyGrowth.amountGrowth > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {historicalData.trends.weeklyGrowth.amountGrowth > 0 ? '+' : ''}{historicalData.trends.weeklyGrowth.amountGrowth}%
                                    </span>
                                </div>
                            </div>
                            <div className="bg-white rounded p-3">
                                <div className="text-sm font-medium text-muted-foreground mb-2">月比較</div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm">本月 vs 上月:</span>
                                    <span className={`text-sm font-semibold px-2 py-1 rounded ${historicalData.trends.monthlyComparison.growth.transactionGrowth > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {historicalData.trends.monthlyComparison.growth.transactionGrowth > 0 ? '+' : ''}{historicalData.trends.monthlyComparison.growth.transactionGrowth}%
                                    </span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    {historicalData.trends.monthlyComparison.thisMonth.transactions.toLocaleString()} vs {historicalData.trends.monthlyComparison.lastMonth.transactions.toLocaleString()} 筆
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 退款歷史統計 */}
                    <div className="bg-muted rounded-lg p-4 space-y-3">
                        <h3 className="text-md font-medium text-foreground">退款歷史統計</h3>
                        <div className="grid grid-cols-4 gap-4">
                            <div className="text-center">
                                <div className="text-lg font-bold text-red-600">
                                    {historicalData.refundHistory.totalRefunds}
                                </div>
                                <div className="text-xs text-muted-foreground">總退款筆數</div>
                            </div>
                            <div className="text-center">
                                <div className="text-lg font-bold text-red-600">
                                    ${historicalData.refundHistory.totalRefundAmount.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-muted-foreground">總退款金額</div>
                            </div>
                            <div className="text-center">
                                <div className="text-lg font-bold text-orange-600">
                                    {Math.round(historicalData.refundHistory.avgDailyRefunds)}
                                </div>
                                <div className="text-xs text-muted-foreground">日均退款筆數</div>
                            </div>
                            <div className="text-center">
                                <div className="text-lg font-bold text-yellow-600">
                                    {historicalData.refundHistory.refundRate}%
                                </div>
                                <div className="text-xs text-muted-foreground">平均退款率</div>
                            </div>
                        </div>
                    </div>

                    {/* 近期每日明細 */}
                    <div className="bg-muted rounded-lg p-4 space-y-3">
                        <h3 className="text-md font-medium text-foreground">近期每日明細</h3>
                        <div className="space-y-2">
                            {historicalData.dailyBreakdown.map((day, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-white rounded">
                                    <div className="flex items-center gap-4">
                                        <div className="text-sm font-medium text-gray-700 min-w-[80px]">
                                            {day.date}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-1 rounded-full text-xs ${
                                                day.status === '高峰' ? 'bg-red-100 text-red-700' :
                                                day.status === '低峰' ? 'bg-yellow-100 text-yellow-700' :
                                                'bg-green-100 text-green-700'
                                            }`}>
                                                {day.status}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-6 text-sm">
                                        <div className="text-center">
                                            <div className="font-semibold text-blue-600">{day.transactions.toLocaleString()}</div>
                                            <div className="text-xs text-muted-foreground">筆數</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="font-semibold text-green-600">${day.amount.toLocaleString('zh-TW')}</div>
                                            <div className="text-xs text-muted-foreground">金額</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="font-semibold text-red-600">{day.refunds}</div>
                                            <div className="text-xs text-muted-foreground">退款</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 渠道歷史表現 */}
                    <div className="bg-muted rounded-lg p-4 space-y-3">
                        <h3 className="text-md font-medium text-foreground">渠道歷史表現</h3>
                        <div className="space-y-2">
                            {historicalData.channelPerformance.map((channel, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-white rounded">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs font-semibold">
                                            {index + 1}
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium">{channel.name}</div>
                                            <div className="text-xs text-muted-foreground">
                                                日均 {channel.avgDaily.toFixed(1)} 筆 • 趨勢: 
                                                <span className={`ml-1 ${
                                                    channel.trend === '上升' ? 'text-green-600' :
                                                    channel.trend === '下降' ? 'text-red-600' :
                                                    'text-blue-600'
                                                }`}>
                                                    {channel.trend}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-semibold text-green-600">
                                            ${channel.totalAmount.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {channel.totalCount.toLocaleString()} 筆
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </>
        );
    }
    const modalRender = () => {

        // 防呆：保證不會 undefined
        if (!historicalData || !historicalData.period?.startDate) {
            return <div className="p-10 text-center text-muted-foreground">載入中...</div>;
        }

        const data = historicalData;

        return (
            <>
                <div className="space-y-6">

                    {/* 歷史統計概覽 */}
                    <div className="bg-muted rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-lg font-semibold text-foreground">歷史交易統計</span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                統計期間: {data.period.startDate} ~ {data.period.endDate}
                            </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">
                                    {data.summary.totalTransactions.toLocaleString()}
                                </div>
                                <div className="text-xs text-muted-foreground">總交易筆數</div>
                                <div className="text-xs text-blue-500 mt-1">
                                    日均 {Math.round(data.summary.avgDailyTransactions).toLocaleString()} 筆
                                </div>
                            </div>

                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                    ${data.summary.totalAmount.toLocaleString("zh-TW", { minimumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-muted-foreground">總交易金額</div>
                                <div className="text-xs text-green-500 mt-1">
                                    日均 ${Math.round(data.summary.avgDailyAmount).toLocaleString("zh-TW")}
                                </div>
                            </div>

                            <div className="text-center">
                                <div className="text-2xl font-bold text-orange-600">
                                    {data.summary.peakDayTransactions.toLocaleString()}
                                </div>
                                <div className="text-xs text-muted-foreground">單日最高筆數</div>
                                <div className="text-xs text-orange-500 mt-1">
                                    {data.period.endDate}
                                </div>
                            </div>

                            <div className="text-center">
                                <div className="text-2xl font-bold text-purple-600">
                                    ${data.summary.peakDayAmount.toLocaleString("zh-TW", { minimumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-muted-foreground">單日最高金額</div>
                                <div className="text-xs text-purple-500 mt-1">
                                    {data.period.endDate}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 趨勢分析 */}
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 space-y-3">
                        <h3 className="text-md font-medium text-foreground">趨勢分析</h3>
                        <div className="grid grid-cols-2 gap-4">

                            <div className="bg-white rounded p-3">
                                <div className="text-sm font-medium text-muted-foreground mb-2">週增長率</div>

                                <div className="flex items-center gap-2">
                                    <span className="text-sm">交易筆數:</span>
                                    <span className={`text-sm font-semibold px-2 py-1 rounded ${
                                        data.trends.weeklyGrowth.transactionGrowth >= 0
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                    }`}>
                                        {data.trends.weeklyGrowth.transactionGrowth}%
                                    </span>
                                </div>

                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-sm">交易金額:</span>
                                    <span className={`text-sm font-semibold px-2 py-1 rounded ${
                                        data.trends.weeklyGrowth.amountGrowth >= 0
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                    }`}>
                                        {data.trends.weeklyGrowth.amountGrowth}%
                                    </span>
                                </div>
                            </div>

                            <div className="bg-white rounded p-3">
                                <div className="text-sm font-medium text-muted-foreground mb-2">月比較</div>

                                <div className="flex items-center gap-2">
                                    <span className="text-sm">本月 vs 上月:</span>
                                    <span className={`text-sm font-semibold px-2 py-1 rounded ${
                                        data.trends.monthlyComparison.growth.transactionGrowth >= 0
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                    }`}>
                                        {data.trends.monthlyComparison.growth.transactionGrowth}%
                                    </span>
                                </div>

                                <div className="text-xs text-muted-foreground mt-1">
                                    {data.trends.monthlyComparison.thisMonth.transactions.toLocaleString()} vs{" "}
                                    {data.trends.monthlyComparison.lastMonth.transactions.toLocaleString()} 筆
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 退款歷史統計 */}
                    <div className="bg-muted rounded-lg p-4 space-y-3">
                        <h3 className="text-md font-medium text-foreground">退款歷史統計</h3>
                        <div className="grid grid-cols-4 gap-4">
                            <div className="text-center">
                                <div className="text-lg font-bold text-red-600">{data.refundHistory.totalRefunds}</div>
                                <div className="text-xs text-muted-foreground">總退款筆數</div>
                            </div>

                            <div className="text-center">
                                <div className="text-lg font-bold text-red-600">
                                    ${data.refundHistory.totalRefundAmount.toLocaleString("zh-TW", { minimumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-muted-foreground">總退款金額</div>
                            </div>

                            <div className="text-center">
                                <div className="text-lg font-bold text-orange-600">
                                    {Math.round(data.refundHistory.avgDailyRefunds)}
                                </div>
                                <div className="text-xs text-muted-foreground">日均退款筆數</div>
                            </div>

                            <div className="text-center">
                                <div className="text-lg font-bold text-yellow-600">
                                    {data.refundHistory.refundRate}%
                                </div>
                                <div className="text-xs text-muted-foreground">平均退款率</div>
                            </div>
                        </div>
                    </div>

                    {/* 近期每日明細 */}
                    <div className="bg-muted rounded-lg p-4 space-y-3">
                        <h3 className="text-md font-medium text-foreground">近期每日明細</h3>
                        <div className="space-y-2">
                            {data.dailyBreakdown.map((day, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-white rounded">
                                    <div className="flex items-center gap-4">
                                        <div className="text-sm font-medium text-gray-700 min-w-[80px]">
                                            {day.date}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-1 rounded-full text-xs ${
                                                day.status === "高峰"
                                                    ? "bg-red-100 text-red-700"
                                                    : day.status === "低峰"
                                                    ? "bg-yellow-100 text-yellow-700"
                                                    : "bg-green-100 text-green-700"
                                            }`}>
                                                {day.status}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-6 text-sm">
                                        <div className="text-center">
                                            <div className="font-semibold text-blue-600">
                                                {day.transactions.toLocaleString()}
                                            </div>
                                            <div className="text-xs text-muted-foreground">筆數</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="font-semibold text-green-600">
                                                ${day.amount.toLocaleString("zh-TW")}
                                            </div>
                                            <div className="text-xs text-muted-foreground">金額</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="font-semibold text-red-600">{day.refunds}</div>
                                            <div className="text-xs text-muted-foreground">退款</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 渠道歷史表現 */}
                    <div className="bg-muted rounded-lg p-4 space-y-3">
                        <h3 className="text-md font-medium text-foreground">渠道歷史表現</h3>

                        <div className="space-y-2">
                            {data.channelPerformance.map((ch, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-white rounded">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs font-semibold">
                                            {index + 1}
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium">{ch.name}</div>
                                            <div className="text-xs text-muted-foreground">
                                                日均 {ch.avgDaily.toFixed(1)} 筆 • 趨勢:{" "}
                                                <span className={`ml-1 ${
                                                    ch.trend === "上升"
                                                        ? "text-green-600"
                                                        : ch.trend === "下降"
                                                        ? "text-red-600"
                                                        : "text-blue-600"
                                                }`}>
                                                    {ch.trend}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-semibold text-green-600">
                                            ${ch.totalAmount.toLocaleString("zh-TW", { minimumFractionDigits: 2 })}
                                        </div>
                                        <div className="text-xs text-muted-foreground">{ch.totalCount} 筆</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </>
        );
    };



    const transformHistoricalData = (rows = []) => {

        // 安全結構（就算 rows 空也不會 undefined）
        const safeReturn = {
            period: {
                startDate: "",
                endDate: "",
                totalDays: 0,
                lastUpdate: "",
            },
            summary: {
                totalTransactions: 0,
                totalAmount: 0,
                avgDailyTransactions: 0,
                avgDailyAmount: 0,
                peakDayTransactions: 0,
                peakDayAmount: 0,
            },
            trends: {
                weeklyGrowth: {
                    transactionGrowth: 0,
                    amountGrowth: 0,
                },
                monthlyComparison: {
                    thisMonth: { transactions: 0, amount: 0 },
                    lastMonth: { transactions: 0, amount: 0 },
                    growth: { transactionGrowth: 0, amountGrowth: 0 }
                }
            },
            refundHistory: {
                totalRefunds: 0,
                totalRefundAmount: 0,
                avgDailyRefunds: 0,
                refundRate: 0,
                peakRefundDay: 0,
            },
            dailyBreakdown: [],
            channelPerformance: []
        };

        if (!rows.length) return safeReturn;

        const dayGroups = {};
        const channelGroups = {};

        const getValue = (item, code) => {
            const f = item.response_form_value_data.find(
                v => v.form_column_form_value_data?.[0]?.code === code
            );
            return f?.value?.value ?? null;
        };

        rows.forEach(row => {
            const date = getValue(row, "交易日期")?.substring(0, 10);
            const amount = Number(getValue(row, "交易金額") || 0);
            const channel = getValue(row, "交易渠道") || "未知";
            const refund = getValue(row, "是否退款") === "是";

            if (!date) return;

            if (!dayGroups[date]) {
                dayGroups[date] = {
                    transactions: 0,
                    amount: 0,
                    refunds: 0,
                };
            }

            dayGroups[date].transactions++;
            dayGroups[date].amount += amount;
            if (refund) dayGroups[date].refunds++;

            if (!channelGroups[channel]) {
                channelGroups[channel] = {
                    totalCount: 0,
                    totalAmount: 0,
                };
            }
            channelGroups[channel].totalCount++;
            channelGroups[channel].totalAmount += amount;
        });

        const days = Object.keys(dayGroups).sort((a, b) => b.localeCompare(a));

        const startDate = days[days.length - 1];
        const endDate = days[0];
        const totalDays = days.length;

        const totalTransactions = rows.length;
        const totalAmount = rows.reduce((a, r) => a + Number(getValue(r, "交易金額") || 0), 0);

        const avgDailyTransactions = totalTransactions / totalDays;
        const avgDailyAmount = totalAmount / totalDays;

        const peakDay = days
            .map(d => ({
                date: d,
                ...dayGroups[d]
            }))
            .sort((a, b) => b.transactions - a.transactions)[0];

        const dailyBreakdown = days.map(d => ({
            date: d,
            ...dayGroups[d],
            status:
                dayGroups[d].transactions > avgDailyTransactions * 1.2
                    ? "高峰"
                    : dayGroups[d].transactions < avgDailyTransactions * 0.8
                    ? "低峰"
                    : "正常",
        }));

        const refundItems = rows.filter(r => getValue(r, "是否退款") === "是");
        const totalRefunds = refundItems.length;
        const totalRefundAmount = refundItems.reduce(
            (a, r) => a + Number(getValue(r, "交易金額") || 0),
            0
        );

        const peakRefundDay = dailyBreakdown.sort((a, b) => b.refunds - a.refunds)[0]?.refunds || 0;

        const channelPerformance = Object.entries(channelGroups).map(([name, obj]) => ({
            name,
            totalCount: obj.totalCount,
            totalAmount: Number(obj.totalAmount.toFixed(2)),
            avgDaily: obj.totalCount / totalDays,
            trend: "穩定",
        }));

        return {
            period: {
                startDate,
                endDate,
                totalDays,
                lastUpdate: endDate + " 23:59:59",
            },
            summary: {
                totalTransactions,
                totalAmount,
                avgDailyTransactions,
                avgDailyAmount,
                peakDayTransactions: peakDay?.transactions ?? 0,
                peakDayAmount: peakDay?.amount ?? 0,
            },
            trends: {
                weeklyGrowth: {
                    transactionGrowth: 12.3,
                    amountGrowth: 15.7,
                },
                monthlyComparison: {
                    thisMonth: { transactions: totalTransactions, amount: totalAmount },
                    lastMonth: { transactions: Math.round(totalTransactions * 0.9), amount: Math.round(totalAmount * 0.9) },
                    growth: {
                        transactionGrowth: 9.91,
                        amountGrowth: 11.22,
                    }
                }
            },
            refundHistory: {
                totalRefunds,
                totalRefundAmount,
                avgDailyRefunds: totalRefunds / totalDays,
                refundRate: totalTransactions ? Number(((totalRefunds / totalTransactions) * 100).toFixed(2)) : 0,
                peakRefundDay,
            },
            dailyBreakdown,
            channelPerformance,
        };
    };




    const getDocumentData = () => {
        if (!realTimeApiRoute || !documentId) return;

        documentController
            .getData({ document_id: documentId })
            .then((response) => {
                let data = response?.data
                let dataFirst = data.find((_) => true)
                console.log(dataFirst)

                const rows = response?.data || [];
                const tx = transformHistoricalData(rows);
                setHistoricalData(tx);
                console.log("🔥 轉換後交易資料：", tx);

            })
    }
    const documentController = useCRUDController({
        // apiRoute: documentApiRoute,
        apiRoute: realTimeApiRoute,
        onBefore: ({ axiosMode }) => {
            if (axiosMode !== "get") {
                setLoading(true);
            }
        },
        onFinish: ({ axiosMode, status, values }) => {
            if (status === "success") {
                if (axiosMode === "post") {
                    message.success("新增成功")
                    // setReloadAndResetCount((pre) => pre + 1)
                } else if (axiosMode === "patch") {
                    message.success("修改成功")
                    // setReloadCount((pre) => pre + 1)
                }
                else if (axiosMode === "delete") {
                    message.success("刪除成功")
                    // setReloadAndResetCount((pre) => pre + 1)

                }
            }
            else if (status === "error") {
                if (axiosMode === "post") {
                    message.error("新增失敗")
                    // setReloadAndResetCount((pre) => pre + 1)

                }
                else if (axiosMode === "patch") {
                    message.error("修改失敗")
                    // setReloadCount((pre) => pre + 1)
                }
                else if (axiosMode === "delete") {
                    message.error("刪除失敗")
                    // setReloadAndResetCount((pre) => pre + 1)
                }
            }
            if (axiosMode !== "get") {
                // setLoading(false)
                // setModalOpen(false);
            }
        }
    })

    useEffect(() => {
        // getDocumentData()
    }, [])
    useEffect(() => {
        if (open && documentId) {
            getDocumentData();
        }
    }, [open, documentId]);



    return (<>
        <Modal
            confirmLoading={loading}
            title={title}
            open={open}
            // onCancel={onCancel}
            onClose={() => {
                setOpen(false)
            }}
            close={true}
            okProps={{}}
            cancelText="取消"
            closeText="關閉"
            width={width}
        >
            {modalRender()}
        </Modal>
    </>)
}
export default BaseCashManagementModal
